import AccountApi from "../generated/api/AccountApi";
import AuthenticationApi from "../generated/api/AuthenticationApi";
import UpdateAccountRequest from "../generated/model/UpdateAccountRequest";
import SaveSubUserRequest from "../generated/model/SaveSubUserRequest";
import AccountProofProperty from "../generated/model/AccountProofProperty";
import UpdateAccountStringOptionRequest from "../generated/model/UpdateAccountStringOptionRequest"
import EnvService from "../env.settings.js";

class AccountService {
    constructor() {
        this.AccountUsers = {
            lastUpdated: null,
            users: [],
        };
        this.AccountApi = new AccountApi();
        this.AuthenticationApi = new AuthenticationApi();
    }

    async GetTempToken() {
        return await window.$A.HandleApiResponse(
            this.AuthenticationApi.getTempToken()
        );
    }

    async DeleteUser(userIdToDelete, userIdToTransferTo) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.deleteSubUser(
                userIdToDelete,
                userIdToTransferTo
            )
        );
    }

    async All() {
        this.AccountUsers.users = await window.$A.HandleApiResponse(
            this.AccountApi.allUsers(null)
        );
        return this.AccountUsers.users;
    }

    async Get() {
        return await window.$A.HandleApiResponse(this.AccountApi.getAccount(null));
    }

    async AccountStats() {
        return await window.$A.HandleApiResponse(
            this.AccountApi.getStats(null)
        );
    }

    async UploadLogoPhoto(dataBlob) {
        let file = new File([dataBlob], "account_logo_photo.png", {
            type: "image/png",
        });
        let body = await window.$A.HandleApiResponse(
            this.AccountApi.uploadAccountLogo({
                file: file,
            }),
            "Your Logo Has Been Updated"
        );
        if (body.length > 10) {
            return body;
        } else {
            return "";
        }
    }

    async UploadWatermarkImage(dataBlob) {
        let file = new File([dataBlob], "account_watermark.png", {
            type: "image/png",
        });
        let body = await window.$A.HandleApiResponse(
            this.AccountApi.uploadAccountWatermark({
                file: file,
            }),
            "Your Watermark Has Been Updated"
        );
        if (body && body.length > 10) {
            return body;
        } else {
            return "";
        }
    }

    async GetWatermarkImage() {
        return await window.$A.HandleApiResponse(
            this.AccountApi.getAccountWatermark(null)
        );
    }

    async RemoveWatermarkImage() {
        return await window.$A.HandleApiResponse(
            this.AccountApi.removeAccountWatermark(null),
            "Watermark Removed"
        );
    }

    async UpdateAccount(name, noReplyEmail, timeZone, dateFormat, whiteLabelDomainName, smtpServerInfo) {
        let account = await window.$A.HandleApiResponse(
            this.AccountApi.updateAccount({
                updateAccountRequest: UpdateAccountRequest.constructFromObject({
                    name: name,
                    noReplyEmail: noReplyEmail,
                    timeZone: timeZone,
                    dateFormat: dateFormat,
                    whiteLabelDomainName: whiteLabelDomainName,
                    smtpServerInfo: smtpServerInfo
                }),
            }),
            "Account Updated"
        );
        return account;
    }

    // The update endpoint treats absent template ids as "clear them", so the
    // current values must ride along or toggling the portal wipes the defaults.
    async SetCustomerPortalEnabled(enabled, defaultProofEmailTemplateId, defaultNewVersionEmailTemplateId) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.updateAccount({
                updateAccountRequest: UpdateAccountRequest.constructFromObject({
                    customerPortalEnabled: enabled,
                    defaultProofEmailTemplateId: defaultProofEmailTemplateId,
                    defaultNewVersionEmailTemplateId: defaultNewVersionEmailTemplateId,
                }),
            }),
            "Portal Setting Saved"
        );
    }

    // Same caveat as SetCustomerPortalEnabled: the current template ids must
    // ride along or the update endpoint clears them.
    async SetSmartReviewEnabled(enabled, defaultProofEmailTemplateId, defaultNewVersionEmailTemplateId) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.updateAccount({
                updateAccountRequest: UpdateAccountRequest.constructFromObject({
                    smartReviewEnabled: enabled,
                    defaultProofEmailTemplateId: defaultProofEmailTemplateId,
                    defaultNewVersionEmailTemplateId: defaultNewVersionEmailTemplateId,
                }),
            }),
            "Smart Review Setting Saved"
        );
    }

    // Same caveat as SetCustomerPortalEnabled: the current template ids must
    // ride along or the update endpoint clears them.
    async SetDefaultFirstApproverApprovesStage(enabled, defaultProofEmailTemplateId, defaultNewVersionEmailTemplateId) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.updateAccount({
                updateAccountRequest: UpdateAccountRequest.constructFromObject({
                    defaultFirstApproverApprovesStage: enabled,
                    defaultProofEmailTemplateId: defaultProofEmailTemplateId,
                    defaultNewVersionEmailTemplateId: defaultNewVersionEmailTemplateId,
                }),
            }),
            "Workflow Default Saved"
        );
    }

    async GetAllPendingInvites() {
        return await window.$A.HandleApiResponse(
            this.AccountApi.getAllPendingUserInvites(null),
            "",
            true
        );
    }

    async DeleteAccount() {
        let response = await window.$A.HandleApiResponse(
            this.AccountApi.deleteAccount(null),
            "Account Deleted"
        );
        if (response !== null) window.$A.LogOut(true);
    }

    async SaveAccountUser(user) {
        if (user.id > 0) {
            return await window.$A.HandleApiResponse(
                this.AccountApi.updateSubUser(user.id, {
                    saveSubUserRequest: SaveSubUserRequest.constructFromObject({
                        name: user.name,
                        title: user.title,
                        email: user.email,
                        phone: user.phone,
                        role: user.role || 2,
                        teams: user.teams
                    }),
                }),
                "User Updated"
            );
        } else {
            return await window.$A.HandleApiResponse(
                this.AccountApi.createSubUser({
                    saveSubUserRequest: SaveSubUserRequest.constructFromObject({
                        name: user.name,
                        title: user.title,
                        email: user.email,
                        phone: user.phone,
                        role: user.role || 2,
                        teams: user.teams
                    }),
                }),
                "User Created. An Invite Email Was Sent To " + user.email
            );
        }
    }

    async GetReviewStylesheet() {
        return this.GetAccountOption("review.stylesheet");
    }

    async SaveReviewStylesheet(style) {
        return this.SaveAccountOption("review.stylesheet", style);
    }

    async GetAccountOption(option) {
        return await window.$A.HandleApiResponse(this.AccountApi.getAccountOption({"option": option}), null, true);
    }

    async SaveAccountSmtpSendMethod(smtpServerInfo) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.saveAccountSmtpSendMethod({smtpServerInfo})
        );
    }

    async SetAccountReviewScreenTheme(screenTheme) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.setReviewScreenTheme({screenTheme})
        );
    }

    async SetDefaultAnnotationTools(tools) {
        const res = await fetch(
            `${this._baseUrl()}/account/default-annotation-tools`,
            {
                method: "POST",
                headers: { ...this._authHeader(), "Content-Type": "application/json" },
                body: JSON.stringify({ defaultAnnotationTools: tools }),
            }
        );
        return res.ok;
    }

    async SaveAccountPostmarkSendMethod() {
        return await window.$A.HandleApiResponse(
            this.AccountApi.saveAccountPostmarkSendMethod(null)
        );
    }

    async GetAccountSendMethodStatus(id, sendType) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.getAccountSendMethodStatus(id, {sendType})
        );
    }

    async GetAccountAllSendMethods() {
        return await window.$A.HandleApiResponse(
            this.AccountApi.getAccountAllSendMethods(null), "", true);
    }

    async ResetAccountSendMethod(sendType) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.resetAccountSendMethod({sendType})
        );
    }

    async SaveAccountOption(option, value) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.setAccountOption(
                {
                    "updateAccountStringOptionRequest": UpdateAccountStringOptionRequest.constructFromObject({
                        option: option,
                        value: value
                    })
                },
            )
        );
    }

    async GetAllCustomProofProperties() {
        return await window.$A.HandleApiResponse(
            this.AccountApi.getAllCustomProofProperties(
                null), "", true);
    }

    async SaveCustomProperty(label, dataType, values, id, accountId) {
        if (id && id > 0) {
            return this.UpdateCustomProperty(id, accountId, values)
        }
        return await window.$A.HandleApiResponse(
            this.AccountApi.createCustomProperty(
                {
                    "accountProofProperty": AccountProofProperty.constructFromObject(
                        {
                            label,
                            dataType,
                            'enum': values,
                            id,
                            accountId
                        }
                    )
                }
            )
        )
    }

    async UpdateCustomProperty(id, accountId, values,) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.updateCustomProperty(id, {
                "requestBody": values
            }, null))
    }

    async DeleteCustomProperty(id) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.deleteCustomProperty(id),
            'Property Deleted',
        );
    }

    async GetChartMetrics(months = 12) {
        return await window.$A.HandleApiResponse(
            this.AccountApi.getChartMetrics(months), "", true
        );
    }

    async GetTimelineEvents() {
        return await window.$A.HandleApiResponse(this.AccountApi.getAccountTimeline(), "", true);
    }

    _baseUrl() {
        return EnvService.instance.apiHost.replace(/\/+$/, "");
    }

    _authHeader() {
        const token = window.$A && window.$A.Auth && window.$A.Auth.apiToken
            ? window.$A.Auth.apiToken.token
            : "";
        return { AccessToken: token };
    }

    async GetOverviewMetrics(startDateIso, endDateIso) {
        const qs = new URLSearchParams();
        if (startDateIso) qs.set("startDate", startDateIso);
        if (endDateIso) qs.set("endDate", endDateIso);
        const res = await fetch(
            `${this._baseUrl()}/account/overview-metrics?${qs}`,
            { headers: this._authHeader() }
        );
        if (!res.ok) return null;
        return res.json();
    }

    async GetTimelineEventsRange(startDateIso, endDateIso, page = 1, resultsPerPage = 100) {
        const qs = new URLSearchParams({ page, resultsPerPage });
        if (startDateIso) qs.set("startDate", startDateIso);
        if (endDateIso) qs.set("endDate", endDateIso);
        const res = await fetch(
            `${this._baseUrl()}/account/timeline?${qs}`,
            { headers: this._authHeader() }
        );
        if (!res.ok) return [];
        return res.json();
    }

    async GetEmailBranding() {
        const res = await fetch(
            `${this._baseUrl()}/account/email-branding`,
            { headers: this._authHeader() }
        );
        if (!res.ok) return null;
        return res.json();
    }

    async SaveEmailBranding(settings) {
        const res = await fetch(
            `${this._baseUrl()}/account/email-branding`,
            {
                method: "POST",
                headers: { ...this._authHeader(), "Content-Type": "application/json" },
                body: JSON.stringify(settings),
            }
        );
        if (!res.ok) {
            window.$A.AlertUser("Could Not Save Email Branding", "warning");
            return null;
        }
        window.$A.AlertUser("Email Branding Saved", "success");
        return res.json();
    }

    async UploadEmailLogoImage(dataBlob) {
        const formData = new FormData();
        formData.append(
            "file",
            new File([dataBlob], "account_email_logo.png", { type: "image/png" })
        );
        const res = await fetch(
            `${this._baseUrl()}/account/email-branding/logo`,
            {
                method: "POST",
                headers: this._authHeader(),
                body: formData,
            }
        );
        if (!res.ok) {
            window.$A.AlertUser("Could Not Upload Email Logo", "warning");
            return "";
        }
        window.$A.AlertUser("Email Logo Updated", "success");
        return res.text();
    }

    async ResetEmailLogoImage() {
        const res = await fetch(
            `${this._baseUrl()}/account/email-branding/reset-logo`,
            {
                method: "POST",
                headers: this._authHeader(),
            }
        );
        return res.ok;
    }

    async UpdateDefaultEmailTemplates(defaultProofEmailTemplateId, defaultNewVersionEmailTemplateId) {
        let account = await window.$A.HandleApiResponse(
            this.AccountApi.updateAccount({
                updateAccountRequest: UpdateAccountRequest.constructFromObject({
                    defaultProofEmailTemplateId: defaultProofEmailTemplateId,
                    defaultNewVersionEmailTemplateId: defaultNewVersionEmailTemplateId
                }),
            }),
            "Default Email Templates Updated"
        );
        return account;
    }
}

export default AccountService;
